import {
    CUSTOM_FIELD_TYPE,
    OPTIONS_FIELD_TYPE,
    OPTIONS_STATUS
} from "../misc/constants";
import { Col, Row } from "reactstrap";
import { useFieldArray, useWatch } from "react-hook-form";

import CheckboxField from "@components/fields/checkbox-field";
import CommonModal from "@components/ui-components/modal";
import FieldArrays from "@components/fields/field-arrays";
import FormView from "@components/views/form-view";
import InputField from "@components/fields/input-field";
import Select from "@components/fields/react-select/select";
import size from "lodash/size";
import useUpsert from "./use-upsert";

const UpsertCustomFields = (props) => {
    const { modalConfig, handleModal } = props;
    const isEdit = !!size(modalConfig.row);

    const { onSubmit, methods, updateLoading, isDirty } = useUpsert(props);
    const {
        control,
        formState: { errors }
    } = methods;

    const { fields, append, remove } = useFieldArray({
        name: "options",
        control
    });

    const fieldType = useWatch({
        control,
        name: "type"
    });

    const showSelectField = fieldType === CUSTOM_FIELD_TYPE.SELECT;
    const showMinMaxField = [
        CUSTOM_FIELD_TYPE.TEXT_AREA,
        CUSTOM_FIELD_TYPE.TEXT
    ].includes(fieldType);
    const showNumberField = fieldType === CUSTOM_FIELD_TYPE.NUMBER;
    const content = [
        {
            leftChild: {
                title: "Enter Basic Details",
                desc : "Enter the basic details of the custom attribute"
            },
            rightChild: (
                <Row>
                    <InputField
                        id="label"
                        name="label"
                        label="Label"
                        required
                        control={control}
                        errors={errors}
                        placeholder="e.g., Industry"/>
                    <Select
                        label="Type"
                        required
                        id="type"
                        name="type"
                        control={control}
                        errors={errors}
                        options={OPTIONS_FIELD_TYPE}/>

                    {fieldType !== CUSTOM_FIELD_TYPE.CHECKBOX && (
                        <>
                            <CheckboxField
                                id="required"
                                name="required"
                                label="Required Field"
                                control={control}
                                errors={errors}/>
                            <InputField
                                id="placeholder"
                                name="placeholder"
                                label="Placeholder"
                                control={control}
                                errors={errors}
                                placeholder="Enter placeholder text"/>
                        </>
                    )}

                    <InputField
                        id="order"
                        name="order"
                        label="Order"
                        type="number"
                        control={control}
                        errors={errors}
                        placeholder="e.g., 1"/>
                    <Select
                        label="Status"
                        id="status"
                        name="status"
                        control={control}
                        errors={errors}
                        options={OPTIONS_STATUS}/>
                </Row>
            )
        },
        {
            leftChild: {
                title: "Validation Rules",
                desc : "Define validation rules for this field"
            },
            rightChild: (
                <Row>
                    {showMinMaxField && (
                        <>
                            <InputField
                                id="validation.minLength"
                                name="validation.minLength"
                                label="Min Length"
                                required
                                type="number"
                                control={control}
                                errors={errors}
                                placeholder="Minimum length"/>
                            <InputField
                                id="validation.maxLength"
                                name="validation.maxLength"
                                label="Max Length"
                                required
                                type="number"
                                control={control}
                                errors={errors}
                                placeholder="Maximum length"/>
                        </>
                    )}
                    {showNumberField && (
                        <>
                            <InputField
                                id="validation.minVal"
                                name="validation.minVal"
                                label="Min Value"
                                required
                                type="number"
                                control={control}
                                errors={errors}
                                placeholder="Minimum value"/>
                            <InputField
                                id="validation.maxVal"
                                name="validation.maxVal"
                                label="Max Value"
                                required
                                type="number"
                                control={control}
                                errors={errors}
                                placeholder="Maximum value"/>
                        </>
                    )}
                </Row>
            ),
            omit: !(showMinMaxField || showNumberField)
        },
        {
            leftChild: {
                title: "Options",
                desc : "Add options for this field"
            },
            rightChild: (
                <FieldArrays
                    render={(index) => (
                        <>
                            <Col md={5}>
                                <InputField
                                    required
                                    id={`label-${index}`}
                                    name={`options.${index}.label`}
                                    label="Label"
                                    control={control}
                                    errors={errors}
                                    placeholder="e.g., Active"/>
                            </Col>
                            <Col md={5}>
                                <InputField
                                    required
                                    id={`value-${index}`}
                                    name={`options.${index}.value`}
                                    label="Value"
                                    control={control}
                                    errors={errors}
                                    placeholder="e.g., active"/>
                            </Col>
                        </>
                    )}
                    fields={fields}
                    subject="custom-fields"
                    handleAppend={() => append({
                        label: "",
                        value: ""
                    })
                    }
                    handleRemove={(index) => {
                        remove(index);
                    }}
                    disabledAppend={false}
                    disabledRemove={false}/>
            ),
            omit: !showSelectField
        }
    ];

    return (
        <CommonModal
            title={`${isEdit ? "Edit" : "Add"} Custom Fields`}
            body={<FormView content={content} />}
            confirmButton={{
                txt    : isEdit ? "Update" : "Add",
                icon   : "content-save",
                onClick: onSubmit,
                loading: updateLoading
            }}
            type="extraLarge"
            isDirty={isDirty}
            handleModal={handleModal}
            isOpen={modalConfig.isOpen}/>
    );
};

export default UpsertCustomFields;
